import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Scale, Eye, Banknote, Handshake, ArrowRight } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { WhatsAppFab } from "@/components/landing/WhatsAppFab";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { IMAGES, SITE } from "@/lib/site";
import { useSEO } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    icon: Handshake,
    title: "Bring it in — or message us first",
    text: "Walk into any showroom, no appointment needed. Prefer to start online? Send photos and details with the form below and we'll prep your visit.",
  },
  {
    icon: Scale,
    title: "We test and weigh in front of you",
    text: "Every piece is tested and weighed openly at the counter — nothing leaves your sight, nothing happens in a back room.",
  },
  {
    icon: Eye,
    title: "You get our honest, best offer",
    text: "Competitive offers, explained line by line. The offer is obligation-free — take it or think it over.",
  },
  {
    icon: Banknote,
    title: "Walk out paid",
    text: "Accept and you're paid on the spot. Most visits take fifteen minutes start to finish.",
  },
];

const CONFIGS = {
  gold: {
    testid: "sell-gold",
    title: "Sell Your Gold Jewelry",
    accent: "Gold",
    img: IMAGES.sellGold,
    alt: "Gold chains and jewelry being evaluated at the counter",
    intro:
      "Broken chains, single earrings, class rings, coins, estate pieces — if it's gold, we'll buy it. 10K, 14K, 18K and beyond, in any condition.",
    buyList: [
      "Chains, bracelets, rings and pendants — even broken or tangled",
      "10K, 14K, 18K and 22K gold, any condition",
      "Gold coins, class rings and estate pieces",
      "Scrap gold and single earrings",
    ],
    interest: "Selling gold",
  },
  diamonds: {
    testid: "sell-diamonds",
    title: "Sell Your Diamond Jewelry",
    accent: "Diamond",
    img: IMAGES.sellDiamonds,
    alt: "A diamond being appraised with a loupe",
    intro:
      "Engagement rings, loose stones, tennis bracelets, earrings — our diamond specialists grade your stones in front of you and make a fair market offer.",
    buyList: [
      "Engagement rings and wedding sets",
      "Loose diamonds, certified or not",
      "Diamond earrings, pendants and tennis bracelets",
      "Estate and inherited diamond jewelry",
    ],
    interest: "Selling diamonds",
  },
};

export default function SellProcessPage({ kind }) {
  const cfg = CONFIGS[kind];
  const [form, setForm] = useState({ name: "", phone: "", email: "", showroom: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: `${cfg.title} | Gold Time Jewelry`,
    canonicalPath: `/sell-${kind}`,
    description: cfg.intro,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message || !form.showroom) {
      toast.error("Please fill in your name, phone, showroom and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        message: form.message,
        interest: cfg.interest,
        showroom: form.showroom,
      });
      toast.success(`Sent to our ${form.showroom.replace(" Showroom", "")} team — they'll be in touch shortly.`);
      setForm({ name: "", phone: "", email: "", showroom: "", message: "" });
    } catch {
      toast.error("Something went wrong — please call or email the showroom instead.");
    }
    setSending(false);
  };

  const inputCls =
    "w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-3 text-neutral-900 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] transition-colors duration-300";

  return (
    <main data-testid={`${cfg.testid}-page`}>
      <Header />
      <section className="relative overflow-hidden pt-[164px] pb-20 lg:pt-[196px] lg:pb-24">
        <PatternLayer pattern={patternLight} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow>We buy, you sell</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid={`${cfg.testid}-heading`}>
              Sell Your <span className="text-[#C9A227]">{cfg.accent} Jewelry</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-neutral-600 leading-relaxed max-w-2xl">{cfg.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={0.06 * i}>
                <div className="bg-white border border-[#E8DCC4] rounded-sm p-6 h-full" data-testid={`${cfg.testid}-step-${i + 1}`}>
                  <span className="text-xs font-semibold text-[#C9A227] uppercase tracking-[0.15em]">Step {i + 1}</span>
                  <s.icon size={24} strokeWidth={1.25} className="text-[#C9A227] mt-3" />
                  <h3 className="font-display text-lg text-neutral-900 mt-3">{s.title}</h3>
                  <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid lg:grid-cols-2 gap-10 items-start">
            <Reveal delay={0.1}>
              <div>
                <div className="rounded-sm overflow-hidden border border-[#E8DCC4]">
                  <img src={cfg.img} alt={cfg.alt} className="w-full h-[280px] object-cover" />
                </div>
                <h2 className="font-display text-2xl text-neutral-900 mt-8">What we buy</h2>
                <ul className="mt-4 space-y-2.5 text-sm text-neutral-600 leading-relaxed list-disc pl-5">
                  {cfg.buyList.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="mt-6 text-sm text-neutral-500">
                  A valid photo ID is required for all sales, as Texas law requires. Every offer is
                  obligation-free.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <form onSubmit={submit} className="bg-white border border-[#E8DCC4] rounded-sm p-7 space-y-4" data-testid={`${cfg.testid}-form`}>
                <h2 className="font-display text-2xl text-neutral-900">Message a showroom</h2>
                <p className="text-sm text-neutral-500">
                  Tell us what you're selling and which showroom works for you — that team replies directly.
                </p>
                <select
                  value={form.showroom}
                  onChange={(e) => setForm({ ...form, showroom: e.target.value })}
                  data-testid={`${cfg.testid}-showroom-select`}
                  className={inputCls}
                >
                  <option value="">Choose your showroom…</option>
                  {SITE.locations.map((loc) => (
                    <option key={loc.name} value={loc.name}>
                      {loc.name} — {loc.lines[1]}
                    </option>
                  ))}
                </select>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    data-testid={`${cfg.testid}-name`}
                    className={inputCls}
                  />
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Phone"
                    data-testid={`${cfg.testid}-phone`}
                    className={inputCls}
                  />
                </div>
                <input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email (optional)"
                  type="email"
                  data-testid={`${cfg.testid}-email`}
                  className={inputCls}
                />
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="What are you selling? Karat, weight, stones, condition — anything you know helps."
                  rows={4}
                  data-testid={`${cfg.testid}-message`}
                  className={inputCls}
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid={`${cfg.testid}-submit`}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-8 py-3.5 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_16px_32px_rgb(201,162,39,0.20)] disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send to showroom"} <ArrowRight size={15} strokeWidth={1.5} />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
      <Footer />
      <WhatsAppFab />
    </main>
  );
}
